<template>
  <div id="DisplaySettings" class="h-screen w-screen flex flex-col overflow-hidden relative display-bg"
    :class="{ 'xl:h-auto xl:w-auto xl:overflow-visible': isDesktop }"
    style="font-family: 'Lato', sans-serif;">

    <!-- Decorative background (mobile only) -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none xl:hidden">
      <div class="absolute -top-20 -right-8 w-56 h-[380px] rotate-[20deg] rounded-[36px] display-deco-top"></div>
      <div class="absolute -bottom-16 -left-12 w-72 h-72 rounded-full display-deco-bottom"></div>
    </div>

    <div class="flex flex-col flex-1 min-h-0 relative z-10" :class="{ 'xl:flex-none': isDesktop }">

      <!-- Header: back + logo (mobile only) -->
      <div v-if="!isDesktop" class="pt-safe flex items-center gap-3 px-safe pb-3 display-header" :class="{ scrolled: isScrolled }">
        <button @click="backTo({ name: 'Profile' })"
          class="w-10 h-10 rounded-xl flex items-center justify-center bg-card display-back-btn active:scale-95 transition-transform duration-150 shrink-0">
          <ArrowLeftIcon class="h-[18px] w-[18px] text-foreground" />
        </button>
        <p class="text-[16px] font-bold text-foreground">{{ $t('profile.display_settings.headline') }}</p>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 min-h-0 overflow-y-auto pb-safe px-safe-flush display-scroll"
        :class="{ 'xl:flex-none xl:overflow-visible xl:pb-0 xl:px-0': isDesktop }"
        @scroll="onScroll">
        <!-- Theme setting -->
        <div class="px-5 mb-4 mt-6">
          <div class="flex items-center gap-2 mb-1 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.display_settings.section_appearance') }}
            </h2>
          </div>
          <p class="text-[12px] text-muted-foreground pl-4 mb-2">{{ $t('profile.display_settings.section_appearance_description') }}</p>
          <div class="display-card rounded-xl overflow-hidden px-4 py-4">
            <div class="flex items-center justify-between">
              <div class="flex-1 mr-4">
                <span class="text-[13px] font-semibold text-foreground">{{ t('profile.theme.toggle_tooltip') }}</span>
                <p class="text-[12px] text-muted-foreground leading-snug mt-0.5">{{ t('profile.display_settings.theme_description') }}</p>
              </div>
              <ThemeToggle />
            </div>
          </div>
        </div>

        <!-- Tax included setting -->
        <div class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-1 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.display_settings.section_data') }}
            </h2>
          </div>
          <p class="text-[12px] text-muted-foreground pl-4 mb-2">{{ $t('profile.display_settings.section_data_description') }}</p>
          <div class="display-card rounded-xl overflow-hidden px-4 py-4">
            <InputToggle v-model="settings.taxIncluded" name="taxIncluded"
              position="right"
              :show-indicator="false"
              :label="t('profile.display_settings.show_tax_included')"
              :description="t('profile.display_settings.show_tax_included_description')" />
          </div>
        </div>

        <!-- Language setting -->
        <div class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-1 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #06C8FF, #009FE3);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.display_settings.section_locale') }}
            </h2>
          </div>
          <p class="text-[12px] text-muted-foreground pl-4 mb-2">{{ $t('profile.display_settings.section_locale_description') }}</p>
          <div class="display-card rounded-xl overflow-hidden">
            <!-- Current language row -->
            <button @click="langOpen = !langOpen"
              class="w-full flex items-center justify-between px-4 py-4 active:scale-[0.99] transition-all duration-100"
              :class="{ 'display-link-divider': langOpen }">
              <div class="flex items-center gap-3">
                <LanguageIcon class="w-5 h-5 shrink-0 text-foreground/50" />
                <div class="text-left">
                  <span class="text-[13px] font-semibold text-foreground">{{ currentLangName }}</span>
                  <span class="text-[12px] text-muted-foreground ml-1.5">{{ currentLangEnglish }}</span>
                </div>
              </div>
              <ChevronDownIcon class="w-4 h-4 text-muted-foreground/40 transition-transform duration-200"
                :class="{ 'rotate-180': langOpen }" />
            </button>
            <!-- Expandable language list -->
            <Transition name="expand">
              <div v-if="langOpen" class="lang-list-wrapper">
                <button v-for="(lang, key) in availableLanguages" :key="key"
                  @click="selectLanguage(lang.code)"
                  class="w-full flex items-center justify-between px-4 py-3.5 active:scale-[0.99] transition-all duration-100 display-link-divider last:border-b-0">
                  <div class="flex items-center gap-3">
                    <div class="w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold uppercase lang-code-badge">
                      {{ lang.code }}
                    </div>
                    <div class="text-left">
                      <span class="text-[14px] font-semibold text-foreground">{{ lang.name }}</span>
                      <span v-if="lang.englishName !== lang.name" class="text-[12px] text-muted-foreground ml-1.5">{{ lang.englishName }}</span>
                      <span v-if="lang.aiGenerated"
                        class="ml-1.5 text-[10px] text-muted-foreground/40 italic">
                        {{ $t('profile.language.ai_generated') }}
                      </span>
                    </div>
                  </div>
                  <Transition name="fade">
                    <div v-if="profileStore.language?.code === lang.code"
                      class="w-6 h-6 rounded-full flex items-center justify-center"
                      style="background: rgba(6, 200, 255, 0.12);">
                      <CheckIcon class="h-3.5 w-3.5" style="color: #06C8FF;" />
                    </div>
                  </Transition>
                </button>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Experimental features setting (hidden until unlocked via About > tap version 5x) -->
        <div v-if="appSettingsStore.settings.experimentalUnlocked" class="px-5 mb-4">
          <div class="flex items-center gap-2 mb-1 pl-1">
            <div class="w-1 h-3.5 rounded-full" style="background: linear-gradient(180deg, #F59E0B, #D97706);"></div>
            <h2 class="text-[13px] font-semibold text-muted-foreground">
              {{ $t('profile.display_settings.section_experimental') }}
            </h2>
          </div>
          <p class="text-[12px] text-muted-foreground pl-4 mb-2">{{ $t('profile.display_settings.section_experimental_description') }}</p>
          <div class="display-card rounded-xl overflow-hidden px-4 py-4">
            <InputToggle v-model="settings.experimentalFeatures" name="experimentalFeatures"
              position="right"
              :show-indicator="false"
              :label="t('profile.display_settings.experimental_toggle')"
              :description="t('profile.display_settings.experimental_toggle_description')" />
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue';
import { useAppSettingsStore } from '../../stores/AppSettingsStore';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, LanguageIcon, ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/outline';
import InputToggle from '../../components/form/input/InputToggle.vue';
import ThemeToggle from '../../components/ThemeToggle.vue';

import { useAppRouter } from '../../composables/useAppRouter';
import { useIsDesktop } from '../../composables/useIsDesktop';
import { useProfileStore } from '../../stores/profileStore';
import { useSnackbarStore } from '../../stores/snackbarStore';
import { availableLanguages } from '../../utils/Languages';

const { locale, t } = useI18n();
const { backTo } = useAppRouter();
const isDesktop = useIsDesktop();
const appSettingsStore = useAppSettingsStore();
const profileStore = useProfileStore();
const snackStore = useSnackbarStore();

const isScrolled = ref(false);
function onScroll(e: Event) {
  isScrolled.value = (e.target as HTMLElement).scrollTop > 8;
}

const settings = ref({
  taxIncluded: false,
  experimentalUnlocked: false,
  experimentalFeatures: false,
});

watch(settings, (value) => {
  appSettingsStore.updateSettings(value);
}, { deep: true });

onMounted(() => {
  settings.value = appSettingsStore.settings;
});

// Language
const langOpen = ref(false);

const currentLangName = computed(() => profileStore.language?.name ?? 'English');
const currentLangEnglish = computed(() => profileStore.language?.englishName ?? 'English');

async function selectLanguage(code: string) {
  if (profileStore.language?.code === code) {
    langOpen.value = false;
    return;
  }
  await profileStore.setLanguage(code);
  locale.value = code;
  langOpen.value = false;
  snackStore.addSnackbar(t('profile.language.changed'), 'success', 1500);
}
</script>

<style scoped>
/* Light mode */
.display-header {
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, backdrop-filter 0.2s ease;
}
.display-header.scrolled {
  border-bottom-color: rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.display-bg {
  background: linear-gradient(175deg, #FFFFFF 0%, #F0F7FA 40%, #F4F4F4 100%);
}
@media (min-width: 1280px) {
  .display-bg { background: transparent; }
}
.display-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.09) 0%, transparent 55%);
}
.display-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.06) 0%, transparent 60%);
}
.display-back-btn {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.display-card {
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.display-link-divider {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.display-link-divider:last-child {
  border-bottom: none;
}
.lang-code-badge {
  background: rgba(6, 200, 255, 0.08);
  color: #06C8FF;
}
.lang-list-wrapper {
  overflow: hidden;
}
.display-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.display-scroll::-webkit-scrollbar {
  display: none;
}

/* Dark mode */
:is(.dark *).display-header.scrolled {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
:is(.dark *).display-bg {
  background: linear-gradient(175deg, #1B1C1E 0%, #1e2528 40%, #1B1C1E 100%);
}
@media (min-width: 1280px) {
  :is(.dark *).display-bg { background: transparent; }
}
:is(.dark *).display-deco-top {
  background: linear-gradient(180deg, rgba(6, 200, 255, 0.15) 0%, transparent 55%);
}
:is(.dark *).display-deco-bottom {
  background: radial-gradient(circle, rgba(6, 200, 255, 0.04) 0%, transparent 50%);
}
:is(.dark *).display-back-btn {
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.05);
}
:is(.dark *).display-card {
  background: #1E2024;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
:is(.dark *).display-link-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
:is(.dark *).display-link-divider:last-child {
  border-bottom: none;
}
:is(.dark *).lang-code-badge {
  background: rgba(6, 200, 255, 0.12);
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  max-height: 300px;
  opacity: 1;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
